import { setPlay } from "./client-errors.js";
import { diagFrame, diagMark, diagPlay } from "./diagnostics.js";
import { gameNumbers } from "./game-numbers.js";
import { effect } from "@preact/signals";
import type {
  Controls,
  GameClient,
  GameManifest,
  MatchOutcome,
  Prediction,
  StartContext,
} from "@obean/game-contract";
import { playProps } from "@obean/game-contract";
import type { MatchDriver } from "./local-driver.js";
import type { AnyGame, GameRef } from "./loader.js";
import { PerfSampler } from "./perf.js";
import { soundOn } from "./sound-pref.js";
import { addPlayed } from "../settings/hour.js";
import { reduceMotion, vibrate, volume } from "../settings/settings.js";
import { feel } from "../feel/kit.js";
import type { Track } from "./telemetry.js";
import { platformOf } from "./telemetry.js";
import type { Replay } from "./replay.js";
import { startReplay } from "./replay.js";
import { Tracer } from "./tracer.js";

/** A traced play (ADR 0035): the room's decision (match.load.trace) or solo's roll. */
export interface TraceStart {
  /** The trace's id: the match and this device's slot. */
  id: string;
  why: "sample" | "switch";
  place: "room" | "solo";
  slot: number;
}

const READY_TIMEOUT_MS = 15_000;
/** perf_sample cadence during a match (docs/metrics.md), plus one at the end. */
const PERF_SAMPLE_MS = 30_000;

type Connection = EventTarget & { type?: string; effectiveType?: string };
/**
 * Which play an event is about: its match id (local play), or for now the room (online: P2-RT-05),
 * and the play's game, version, mission and mode (P3-OBS-02).
 */
type MatchIds = Readonly<Record<string, string>>;
const nextFrame = () => new Promise<number>((resolve) => requestAnimationFrame(resolve));

/**
 * Hosts one game client: owns its mount element, keeps it sized, runs the display loop, and reports
 * gameplay_started on the first rendered frame (P2-WEB-04). During a match it reports perf_sample,
 * visibility_changed and network_changed (P2-AN-04), and a Learn game's answers (quiz_answered).
 */
/**
 * The pixel density games draw at: the screen's, at most 2. Phones report 2.6-3.5, and drawing at
 * that costs their GPUs ~1.7-3x the pixels for a difference hard to see (quality pass, 2026-10-08:
 * the founder's Android phone drew under 20 fps on staging).
 */
export const MAX_PIXEL_RATIO = 2;
export const renderPixelRatio = (screen: number) =>
  Math.min(MAX_PIXEL_RATIO, Number.isFinite(screen) && screen > 0 ? screen : 1);

/** At most this many reports of one issue code per page: a game failing in a loop is one issue. */
export const ISSUES_PER_CODE = 3;
const issuesSent = new Map<string, number>();

/**
 * A game's own issue report (P3-OBS-04): only codes its manifest declares, with their severity,
 * the game and version, and the play it happened in if one is on.
 */
export function issueReporter(
  manifest: Pick<GameManifest, "id" | "version" | "issues">,
  track: Track,
  play: () => Readonly<Record<string, string>> | null,
): (code: string) => void {
  return (code) => {
    const declared = manifest.issues?.find((i) => i.code === code);
    if (!declared) return;
    const key = `${manifest.id}:${code}`;
    const sent = issuesSent.get(key) ?? 0;
    if (sent >= ISSUES_PER_CODE) return;
    issuesSent.set(key, sent + 1);
    track("game_issue", {
      game_id: manifest.id,
      game_version: manifest.version,
      ...play(),
      code,
      severity: declared.severity,
    });
  };
}

export class GameHost {
  readonly #client: GameClient<unknown, unknown, HTMLElement>;
  readonly #mount: HTMLDivElement;
  readonly #track: Track;
  readonly #bundleLoadMs: number;
  readonly #manifest: GameManifest;
  #observer: ResizeObserver | null = null;
  #raf = 0;
  #running = false;
  /** When the match running here started, for the hour's note; null when none is. */
  #playedFrom: number | null = null;
  /** Ends the match's measuring: a last perf_sample, and the listeners removed. */
  #stopMeasuring: (() => void) | null = null;
  /** Stops following the sound setting and the page's visibility. */
  #stopSound: (() => void) | null = null;
  /** Ends a traced play's recording and uploads it (once). */
  #endTrace: ((how: string) => void) | null = null;
  /** The play on now (its ids and play properties), for a game's issue report; null between plays. */
  #play: Readonly<Record<string, string>> | null = null;

  private constructor(
    client: GameClient<unknown, unknown, HTMLElement>,
    mount: HTMLDivElement,
    track: Track,
    bundleLoadMs: number,
    manifest: GameManifest,
  ) {
    this.#client = client;
    this.#mount = mount;
    this.#track = track;
    this.#bundleLoadMs = bundleLoadMs;
    this.#manifest = manifest;
  }

  /**
   * Creates the game's client inside `container`, waits until it can draw, and starts following the
   * container's size. `loadStartedAt` is when loading the bundle began (for bundle_load_ms).
   */
  static async load(options: {
    container: HTMLElement;
    game: AnyGame;
    ref: GameRef;
    track: Track;
    loadStartedAt: number;
  }): Promise<GameHost> {
    const mount = document.createElement("div");
    mount.className = "game-mount";
    options.container.append(mount);
    const client = options.game.createClient();
    // The host isn't made until the game has loaded, but the game may report an issue while loading.
    let made: GameHost | null = null;
    await client.load({
      mount,
      assetsBase: `/games/${options.ref.id}/${options.ref.version}/`,
      devicePixelRatio: renderPixelRatio(window.devicePixelRatio),
      reportIssue: issueReporter(options.game.manifest, options.track, () =>
        made === null ? null : made.#play,
      ),
      // The player's settings (P5-UX-07, ADR 0047): a buzz only if they allow it, less motion if
      // they or their phone ask.
      vibrate,
      reduceMotion: () => reduceMotion.peek(),
      feel, // Obean's own sounds and buzzes (P5-UX-02, ADR 0048)
    });
    const deadline = performance.now() + READY_TIMEOUT_MS;
    while (!client.ready()) {
      if ((await nextFrame()) > deadline) throw new Error("the game didn't get ready in time");
    }
    const host = new GameHost(
      client,
      mount,
      options.track,
      Math.round(performance.now() - options.loadStartedAt),
      options.game.manifest,
    );
    made = host;
    host.#follow();
    host.#followSound();
    return host;
  }

  #follow(): void {
    const resize = () => {
      const rect = this.#mount.getBoundingClientRect();
      this.#client.resize({
        width: rect.width,
        height: rect.height,
        devicePixelRatio: renderPixelRatio(window.devicePixelRatio),
      });
    };
    this.#observer = new ResizeObserver(resize);
    this.#observer.observe(this.#mount);
    resize();
  }

  /** The game's own input for these device-neutral controls (sample once per simulation step). */
  input(controls: Controls): unknown {
    return this.#client.input(controls);
  }

  /** How the game predicts its own player online, if it does. */
  get prediction(): Prediction<unknown> | undefined {
    return this.#client.prediction;
  }

  get bundleLoadMs(): number {
    return this.#bundleLoadMs;
  }

  /**
   * Starts the match and the display loop; `onEnd` runs once when the driver reports an outcome.
   * Online matches don't know the server's match id yet, so their events carry the room id.
   */
  start(
    ctx: StartContext & { matchId: string | null; roomId?: string; trace?: TraceStart },
    driver: MatchDriver<unknown>,
    onEnd?: (outcome: MatchOutcome) => void,
  ): void {
    this.stop(); // a match already running here (a reconnect reloads it)
    this.#client.start(ctx);
    this.#running = true;
    this.#playedFrom = performance.now(); // the hour's note (ADR 0043) counts play time
    // What every play reports (P5-AN-04), on its health events too (P3-OBS-02).
    const play = playProps(
      this.#manifest,
      ctx.missionId,
      ctx.players.some((p) => p.team !== undefined),
    );
    setPlay(play); // client_error's during_match and game
    const ids: MatchIds = {
      ...(ctx.matchId !== null
        ? { match_id: ctx.matchId }
        : ctx.roomId !== undefined
          ? { room_id: ctx.roomId }
          : {}),
      ...play,
    };
    this.#play = ids;
    // The last two minutes, for "Send diagnostics" on the contact form (P3-OBS-06).
    const device = platformOf(
      navigator.userAgent,
      navigator.maxTouchPoints,
      matchMedia("(pointer: coarse)").matches,
      Math.min(screen.width, screen.height),
    );
    diagPlay(
      ctx.roomId !== undefined ? "room" : "solo",
      ctx.mySlot,
      { ...play, ...(ctx.matchId !== null ? { match_id: ctx.matchId } : {}) },
      { device_class: device.device_class, os: device.os, browser: device.browser },
    );
    const perf = this.#measure(ids, driver);
    const tracing = ctx.trace ? this.#trace(ctx.trace, play, ctx.matchId, driver) : null;
    /** A Learn game's items already answered this match: each is recorded once (P5-AN-04). */
    const answered = new Set<string>();
    let first = true;
    let sampledAt = performance.now();
    const loop = (now: number) => {
      if (!this.#running) return;
      const frame = driver.frame(now);
      this.#client.render(frame);
      perf.frame(now);
      tracing?.(now);
      diagFrame(now, () => ({ ...driver.netNow?.(), ...this.#gameNow() }));
      const answer = this.#client.answered?.(frame.to, ctx.mySlot);
      if (answer && !answered.has(answer.item)) {
        answered.add(answer.item);
        this.#track("quiz_answered", {
          ...ids,
          mode: driver.mode,
          ...answer,
        });
      }
      if (now - sampledAt >= PERF_SAMPLE_MS) {
        sampledAt = now;
        this.#sample(perf, ids, driver);
      }
      if (first) {
        first = false;
        this.#track("gameplay_started", {
          ...ids,
          mode: driver.mode,
          bundle_load_ms: this.#bundleLoadMs,
        });
      }
      if (frame.outcome) {
        this.#running = false;
        this.#stopMeasuring?.();
        this.#endTrace?.(`end:${frame.outcome.result}`);
        diagMark(`end:${frame.outcome.result}`);
        this.#countPlayed();
        feel(frame.outcome.result === "win" ? "win" : "lose"); // every game's end sounds alike
        this.#client.result(frame.outcome);
        onEnd?.(frame.outcome);
        return;
      }
      this.#raf = requestAnimationFrame(loop);
    };
    this.#raf = requestAnimationFrame(loop);
  }

  #sample(perf: PerfSampler, ids: MatchIds, driver: MatchDriver<unknown>): void {
    const sample = perf.take();
    if (sample)
      this.#track("perf_sample", {
        ...ids,
        mode: driver.mode,
        ...sample,
        ...driver.netStats?.(),
        ...this.#gameNow(),
      });
  }

  /** The game's own numbers now (P3-OBS-04), as `game_<name>`. */
  #gameNow(): Record<string, number> {
    return gameNumbers(this.#client.diagnostics?.bind(this.#client));
  }

  /**
   * Records a traced play (ADR 0035): each frame's time, and once a second the network, memory and
   * long tasks; marks for the page hiding, the network, errors. Returns the per-frame hook; the
   * trace uploads once, when the play ends or stops. Numbers and names only: no person.
   */
  #trace(
    trace: TraceStart,
    play: Record<string, string>,
    matchId: string | null,
    driver: MatchDriver<unknown>,
  ): (now: number) => void {
    const start = performance.now();
    const tracer = new Tracer(start);
    // A masked screen replay goes with it, where crash reports are on (ADR 0035, decision 5).
    let replay: Replay | null = null;
    void startReplay().then((r) => {
      replay = r;
    });
    let lastFrame: number | null = null;
    let lastSecond = start;
    let longTasks = 0;
    let longTaskMs = 0;
    let observer: PerformanceObserver | null = null;
    if (PerformanceObserver.supportedEntryTypes.includes("longtask")) {
      observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          longTasks++;
          longTaskMs += entry.duration;
        }
      });
      observer.observe({ type: "longtask" });
    }
    const memory = () => {
      const used = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory
        ?.usedJSHeapSize;
      return used === undefined ? null : Math.round(used / 1_048_576);
    };
    const mark = (what: string) => {
      tracer.mark(performance.now(), what);
    };
    const onVisibility = () => {
      mark(document.visibilityState === "hidden" ? "hidden" : "visible");
    };
    const onOnline = () => {
      mark("online");
    };
    const onOffline = () => {
      mark("offline");
    };
    const onError = (e: ErrorEvent) => {
      mark(`error:${e.error instanceof Error ? e.error.name.toLowerCase() : "unknown"}`);
    };
    // A tab closed mid-play is the commonest end of all: send what was recorded as it goes.
    const onPageHide = () => {
      this.#endTrace?.("pagehide");
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("error", onError);
    window.addEventListener("pagehide", onPageHide);
    this.#endTrace = (how) => {
      this.#endTrace = null;
      mark(how);
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("error", onError);
      window.removeEventListener("pagehide", onPageHide);
      const platform = platformOf(
        navigator.userAgent,
        navigator.maxTouchPoints,
        matchMedia("(pointer: coarse)").matches,
        Math.min(screen.width, screen.height),
      );
      const replayId = replay?.id();
      void replay?.stop().catch(() => undefined);
      const body = JSON.stringify({
        ...(replayId && /^[A-Za-z0-9-]{1,64}$/.test(replayId) ? { replay_id: replayId } : {}),
        trace_id: trace.id,
        why: trace.why,
        place: trace.place,
        play: { ...play, ...(matchId ? { match_id: matchId } : {}), slot: trace.slot },
        platform: {
          device_class: platform.device_class,
          os: platform.os,
          browser: platform.browser,
        },
        ...tracer.finish(performance.now()),
      });
      // keepalive lets it outlive a closing page, for bodies under its 64 KB limit.
      void fetch("/api/traces", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: body.length < 60_000,
      }).catch(() => {
        // A lost trace is only a lost sample.
      });
    };
    return (now) => {
      if (lastFrame !== null) tracer.frame(now - lastFrame);
      lastFrame = now;
      if (now - lastSecond < 1000) return;
      lastSecond = now;
      tracer.second(now, {
        ...driver.netNow?.(),
        memory_mb: memory(),
        long_tasks_total: longTasks,
        long_task_ms_total: Math.round(longTaskMs),
        ...this.#gameNow(),
      });
    };
  }

  /** Frame sampling and the tab and network listeners for one match. */
  #measure(ids: MatchIds, driver: MatchDriver<unknown>): PerfSampler {
    const perf = new PerfSampler();
    const onVisibility = () => {
      const hidden = document.visibilityState === "hidden";
      this.#track("visibility_changed", { ...ids, hidden, during_match: true });
      if (!hidden) return;
      driver.release?.(); // no frames run while hidden, so the last input would be held (P2-WEB-06)
      // The tab may never come back (closing a tab hides it first): report what was measured,
      // then stop counting until it's shown again.
      this.#sample(perf, ids, driver);
      perf.pause();
    };
    const connection = (navigator as Navigator & { connection?: Connection }).connection;
    const onNetwork = () => {
      this.#track("network_changed", {
        ...ids,
        online: navigator.onLine,
        type: connection?.type ?? connection?.effectiveType ?? null,
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onNetwork);
    window.addEventListener("offline", onNetwork);
    connection?.addEventListener("change", onNetwork);
    this.#stopMeasuring = () => {
      this.#stopMeasuring = null;
      this.#sample(perf, ids, driver);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onNetwork);
      window.removeEventListener("offline", onNetwork);
      connection?.removeEventListener("change", onNetwork);
    };
    return perf;
  }

  /** Stops a match that won't finish (called off, or left); the game stays loaded for the next. */
  stop(): void {
    this.#stopMeasuring?.(); // report what was measured
    this.#endTrace?.("stopped");
    if (this.#running) diagMark("stopped");
    this.#countPlayed(); // a match left before its end counts too
    this.#play = null;
    this.#running = false;
    setPlay(null);
    cancelAnimationFrame(this.#raf);
  }

  /**
   * The game is heard (P2-TANK-08) when the player hasn't muted it and the page is in front; in the
   * background it's suspended, and back in front it resumes (P2-WEB-06).
   */
  #followSound(): void {
    const apply = (on: boolean, loud: number) => {
      this.#client.sound?.({ on: on && document.visibilityState === "visible", volume: loud });
    };
    const onVisibility = () => {
      apply(soundOn.peek(), volume.peek());
    };
    const stopEffect = effect(() => {
      apply(soundOn.value, volume.value); // runs again whenever the switch or the volume changes
    });
    document.addEventListener("visibilitychange", onVisibility);
    this.#stopSound = () => {
      stopEffect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }

  /** Tears the game down; the page may load another game next. */
  #countPlayed(): void {
    if (this.#playedFrom === null) return;
    addPlayed(performance.now() - this.#playedFrom);
    this.#playedFrom = null;
  }

  exit(): void {
    this.stop();
    this.#stopSound?.();
    this.#observer?.disconnect();
    this.#client.exit();
    this.#mount.remove();
  }
}
